import {parseInfoLine} from './parser';
import {StockfishEngineClient} from './engine';

export type EngineEvalLine = {
  rank: number;
  kind: 'cp' | 'mate';
  val: number;
  depth: number;
  san: string[];
  pvUci: string[];
  scoreCp: number;
};

export type PositionEval = {
  best1: {kind: 'cp' | 'mate'; val: number; depth: number} | null;
  bestUci: string;
  pvSan: string[];
  lines: EngineEvalLine[];
};

export type AnalyzeFenOptions = {
  depth?: number;
  movetime?: number;
  multiPV?: number;
  timeoutMs?: number;
};

const DEFAULT_TIMEOUT_MS = 8000;
const PER_POSITION_TIMEOUT_MESSAGE =
  'browser engine: per-position search timeout';

function scoreToCp(kind: 'cp' | 'mate', val: number): number {
  if (kind === 'cp') {
    return val;
  }
  return Math.sign(val) * (100000 - Math.abs(val));
}

export function analyzeFenWithClient(
  engine: StockfishEngineClient,
  fen: string,
  opts: AnalyzeFenOptions,
): Promise<PositionEval> {
  return new Promise<PositionEval>((resolve, reject) => {
    const linesByRank = new Map<number, EngineEvalLine>();
    let bestUci = '';
    let activeToken = -1;
    let settled = false;

    const timer = setTimeout(() => {
      if (settled) {
        return;
      }
      settled = true;
      try {
        engine.stop();
      } catch {
        /* best effort */
      }
      cleanup();
      reject(new Error(PER_POSITION_TIMEOUT_MESSAGE));
    }, opts.timeoutMs || DEFAULT_TIMEOUT_MS);

    const cleanup = () => {
      clearTimeout(timer);
      engine.setCallbacks({});
    };

    engine.setCallbacks({
      onInfo: ({raw, token}) => {
        if (token !== activeToken) {
          return;
        }
        const parsed = parseInfoLine(raw, fen);
        if (!parsed) {
          return;
        }
        const val = parseInt(parsed.score, 10);
        if (Number.isNaN(val)) {
          return;
        }
        const depth = Number((raw.match(/\bdepth (\d+)/) || [])[1]) || 0;
        const kind = parsed.isMate ? 'mate' : 'cp';
        linesByRank.set(parsed.variationNumber, {
          rank: parsed.variationNumber,
          kind,
          val,
          depth,
          san: parsed.movesSAN,
          pvUci: parsed.movesUci,
          scoreCp: scoreToCp(kind, val),
        });
      },
      onBestMove: (move, token) => {
        if (token !== activeToken || settled) {
          return;
        }
        settled = true;
        bestUci = move || '';
        cleanup();
        const lines = Array.from(linesByRank.values()).sort(
          (a, b) => a.rank - b.rank,
        );
        const bestLine = lines.find(line => line.rank === 1) || lines[0];
        resolve({
          best1: bestLine
            ? {kind: bestLine.kind, val: bestLine.val, depth: bestLine.depth}
            : null,
          bestUci,
          pvSan: bestLine?.san || [],
          lines,
        });
      },
      onError: err => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        reject(err instanceof Error ? err : new Error('engine error'));
      },
    });

    activeToken = engine.analyze(fen, {
      depth: opts.depth,
      movetime: opts.movetime,
      multiPV: opts.multiPV,
    });
  });
}

type QueuedJob = {
  fen: string;
  opts: AnalyzeFenOptions;
  resolve: (value: PositionEval) => void;
  reject: (reason?: unknown) => void;
};

type Lane = {
  engine: StockfishEngineClient;
  busy: boolean;
};

export class EnginePool {
  private lanes: Lane[] = [];
  private queue: QueuedJob[] = [];
  private destroyed = false;

  async init(size: number): Promise<void> {
    const laneCount = Math.max(1, Math.floor(size || 1));
    const engines: Lane[] = [];
    try {
      for (let i = 0; i < laneCount; i += 1) {
        const engine = new StockfishEngineClient();
        await engine.init();
        engine.newGame();
        engines.push({engine, busy: false});
      }
      this.lanes = engines;
    } catch (err) {
      engines.forEach(lane => lane.engine.destroy());
      throw err;
    }
  }

  run(fen: string, opts: AnalyzeFenOptions): Promise<PositionEval> {
    if (this.destroyed) {
      return Promise.reject(new Error('engine pool destroyed'));
    }
    return new Promise<PositionEval>((resolve, reject) => {
      this.queue.push({fen, opts, resolve, reject});
      this.pump();
    });
  }

  destroy(): void {
    this.destroyed = true;
    this.queue.splice(0).forEach(job => {
      job.reject(new Error('engine pool destroyed'));
    });
    this.lanes.forEach(lane => lane.engine.destroy());
    this.lanes = [];
  }

  private pump(): void {
    if (this.destroyed) {
      return;
    }
    const lane = this.lanes.find(candidate => !candidate.busy);
    const job = this.queue.shift();
    if (!lane || !job) {
      if (job) {
        this.queue.unshift(job);
      }
      return;
    }

    lane.busy = true;
    const runJob = async () => {
      try {
        const result = await analyzeFenWithClient(
          lane.engine,
          job.fen,
          job.opts,
        );
        job.resolve(result);
      } catch (err) {
        job.reject(err);
        // Recover the lane on ANY engine-level failure — per-position timeout,
        // worker crash, or onError. Previously only timeouts were recovered, so
        // a crashed engine was left in place and every subsequent job routed to
        // this lane would fail too (poisoning ~1/K of the analysis).
        try {
          await this.recoverLane(lane);
        } catch {
          /* keep the pool alive; the next job will surface engine readiness */
        }
      } finally {
        lane.busy = false;
        this.pump();
      }
    };
    runJob();
  }

  private async recoverLane(lane: Lane): Promise<void> {
    if (this.destroyed) {
      return;
    }
    const oldEngine = lane.engine;
    const engine = new StockfishEngineClient();
    await engine.init();
    engine.newGame();
    lane.engine = engine;
    try {
      oldEngine.destroy();
    } catch {
      /* noop */
    }
  }
}
